import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Banknote,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Gauge,
  MessageCircle,
  Plus,
  RefreshCcw,
  Search,
  ShieldAlert,
  UserPlus,
  UserCheck,
  Users,
  UserX,
  X,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/askilia/layout-bits";
import {
  BookingStateBadge,
  RiskBadge,
  Chip,
  BOOKING_STATE,
  ChannelBadge,
  SourceTag,
  AttendanceBadge,
} from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  fmtTime,
  decimalHour,
  sameDay,
  uid,
} from "@/lib/format";
import {
  activeBooking,
  bookingAssignments,
  bookingDurationMinutes,
  bookingResourceIds,
  findBookingConflictDetails,
  findBookingConflicts,
  findDutyAssignmentIssue,
  findDutyRosterConflicts,
  resolveDutyAssignment,
  getAttendanceSignal,
  isActionableScheduleBooking,
  dutyMinutesForRooms,
  roomDutyBlocks,
} from "@/lib/scheduling";

const START = 10;
const END = 19;
const HOUR = 96;
const SLOT_MINUTES = 15;
const FIELD_CLASS = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20";

const STATE_STYLE = {
  hold: "border-warning/60 bg-warning-soft text-warning-soft-foreground [background-image:repeating-linear-gradient(45deg,transparent,transparent_6px,hsl(var(--warning)/0.08)_6px,hsl(var(--warning)/0.08)_12px)]",
  pending: "border-warning/50 bg-warning-soft text-warning-soft-foreground",
  confirmed: "border-primary/40 bg-primary-soft text-primary-soft-foreground",
  completed: "border-success/40 bg-success-soft text-success-soft-foreground",
  cancelled: "border-border bg-muted text-muted-foreground line-through opacity-70",
  noshow: "border-destructive/40 bg-destructive-soft text-destructive-soft-foreground",
};

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeek(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  const day = (next.getDay() + 6) % 7;
  next.setDate(next.getDate() - day);
  return next;
}

function startOfMonth(date) {
  const next = new Date(date.getFullYear(), date.getMonth(), 1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function monthDays(date) {
  const first = startOfMonth(date);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return Array.from({ length: count }, (_, index) => addDays(first, index));
}

function monthGridDays(date) {
  const first = startOfWeek(startOfMonth(date));
  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
}

function isoWeekNumber(date) {
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));
  return 1 + Math.round((target - firstThursday) / 604800000);
}

function layoutWeekBookings(bookings) {
  const sorted = [...bookings].sort((a, b) => new Date(a.start) - new Date(b.start));
  const groups = [];
  let group = [];
  let groupEnd = 0;

  const finishGroup = () => {
    if (!group.length) return;
    const laneEnds = [];
    const placed = group.map((booking) => {
      const start = new Date(booking.start).getTime();
      const end = new Date(booking.end).getTime() + Number(booking.buffer || 0) * 60000;
      let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = end;
      return { booking, lane };
    });
    const laneCount = Math.max(1, laneEnds.length);
    groups.push(...placed.map((item) => ({ ...item, laneCount })));
    group = [];
  };

  sorted.forEach((booking) => {
    const start = new Date(booking.start).getTime();
    const end = new Date(booking.end).getTime() + Number(booking.buffer || 0) * 60000;
    if (group.length && start >= groupEnd) finishGroup();
    group.push(booking);
    groupEnd = Math.max(groupEnd, end);
  });
  finishGroup();
  return groups;
}

function moveSlotFocus(event, columnIndex, slotIndex, columnCount, slotCount) {
  const movement = {
    ArrowDown: [0, 1],
    ArrowUp: [0, -1],
    ArrowRight: [1, 0],
    ArrowLeft: [-1, 0],
  }[event.key];
  if (!movement && event.key !== "Home" && event.key !== "End") return;

  event.preventDefault();
  const nextColumn = Math.min(columnCount - 1, Math.max(0, columnIndex + (movement?.[0] || 0)));
  const nextSlot = event.key === "Home"
    ? 0
    : event.key === "End"
      ? slotCount - 1
      : Math.min(slotCount - 1, Math.max(0, slotIndex + (movement?.[1] || 0)));
  event.currentTarget
    .closest("[data-slot-grid]")
    ?.querySelector(`[data-slot-column="${nextColumn}"][data-slot-index="${nextSlot}"]`)
    ?.focus();
}

function dateInputValue(value) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function timeInputValue(value) {
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function localDateTime(date, time) {
  if (!date || !time) return null;
  const value = new Date(`${date}T${time}:00`);
  return Number.isNaN(value.getTime()) ? null : value;
}

function slotTime(hour) {
  const whole = Math.floor(hour);
  const minutes = Math.round((hour - whole) * 60);
  return `${String(whole).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function nextDefaultStart(cursor, services) {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  let date = new Date(cursor);
  date.setHours(0, 0, 0, 0);
  if (date < today) date = new Date(today);

  const longestHold = Math.max(...services.map((service) => Number(service.duration || 0) + Number(service.buffer || 0)), 30);
  const latestStartMinutes = END * 60 - longestHold;
  let time = "10:00";

  if (sameDay(date, now)) {
    const roundedMinutes = Math.ceil((now.getHours() * 60 + now.getMinutes()) / SLOT_MINUTES) * SLOT_MINUTES;
    if (roundedMinutes <= latestStartMinutes) {
      time = slotTime(Math.max(START * 60, roundedMinutes) / 60);
    } else {
      date = addDays(date, 1);
    }
  }

  while (date.getDay() === 0) date = addDays(date, 1);
  return { date, time };
}

function bookingMatchesResource(booking, resourceId, services) {
  return bookingResourceIds(booking, services).includes(resourceId);
}

function serviceDoctorIds(service, duties = []) {
  const configured = (service?.doctorOptions?.length ? service.doctorOptions : [service?.resource]).filter(Boolean);
  const rostered = (duties || [])
    .filter((duty) => Array.isArray(duty.serviceIds) && duty.serviceIds.includes(service?.id))
    .map((duty) => duty.doctorId)
    .filter(Boolean);
  return Array.from(new Set([...configured, ...rostered]));
}

function serviceRoomIds(service, duties = []) {
  const configured = (service?.roomOptions?.length ? service.roomOptions : [service?.room]).filter(Boolean);
  const rostered = (duties || [])
    .filter((duty) => Array.isArray(duty.serviceIds) && duty.serviceIds.includes(service?.id))
    .map((duty) => duty.roomId)
    .filter(Boolean);
  return Array.from(new Set([...configured, ...rostered]));
}

function dutyBoundary(date, value) {
  if (value instanceof Date) return value;
  if (typeof value === "string" && value.includes("T")) return new Date(value);
  const [hours = 0, minutes = 0] = String(value || "00:00").split(":").map(Number);
  const boundary = new Date(date);
  boundary.setHours(hours, minutes, 0, 0);
  return boundary;
}

function normalizedRoomDuties(duties, date, roomId) {
  return roomDutyBlocks(duties || [], date, roomId).map((duty) => ({
    ...duty,
    startDate: dutyBoundary(date, duty.startDate || duty.start),
    endDate: dutyBoundary(date, duty.endDate || duty.end),
  }));
}

function normalizedResourceDuties(duties, date, resourceId, planningLens) {
  return normalizedRoomDuties(duties, date, null).filter((duty) => (
    planningLens === "rooms" ? duty.roomId === resourceId : duty.doctorId === resourceId
  ));
}

function dutyCoveringHour(duties, date, roomId, hour) {
  const slot = new Date(date);
  slot.setHours(Math.floor(hour), Math.round((hour % 1) * 60), 0, 0);
  return normalizedRoomDuties(duties, date, roomId)
    .find((duty) => duty.startDate <= slot && duty.endDate > slot);
}

function dutyCoveringResourceHour(duties, date, resourceId, planningLens, hour) {
  const slot = new Date(date);
  slot.setHours(Math.floor(hour), Math.round((hour % 1) * 60), 0, 0);
  return normalizedResourceDuties(duties, date, resourceId, planningLens)
    .find((duty) => duty.startDate <= slot && duty.endDate > slot);
}

function conflictSummary(details, state, t, localize) {
  if (!details.length) return "";
  const first = details[0];
  const names = first.sharedResourceIds
    .map((id) => localize(state.resources.find((resource) => resource.id === id)?.short))
    .filter(Boolean);
  const customer = state.customers.find((item) => item.id === first.booking.customerId);
  const extra = details.length > 1 ? t("calendar.conflicts.more", { count: details.length - 1 }) : "";
  return t(first.type === "buffer" ? "calendar.conflicts.bufferSummary" : "calendar.conflicts.bookingSummary", {
    resource: names.join(" · ") || t("calendar.conflicts.resourceFallback"),
    customer: localize(customer?.name) || t("calendar.conflicts.otherCustomer"),
    time: fmtTime(first.booking.start),
    extra,
  });
}

function localizedDutyIssueMessage(issue, resources, t, localize, formatDate) {
  if (!issue) return "";
  const resourceById = new Map(resources.map((resource) => [resource.id, resource]));
  const nameOf = (id, fallbackKey) => localize(resourceById.get(id)?.short || resourceById.get(id)?.name) || t(fallbackKey);
  const doctor = nameOf(issue.doctorId, "calendar.duty.selectedDoctor");
  const room = nameOf(issue.roomId, "calendar.duty.selectedRoom");
  const alternateRoom = issue.coveringDuty?.roomId
    ? nameOf(issue.coveringDuty.roomId, "calendar.duty.anotherRoom")
    : null;
  const time = (value) => value instanceof Date && !Number.isNaN(value.getTime()) ? fmtTime(value) : null;

  if (issue.type === "doctor_overlap" || issue.type === "room_overlap") {
    const date = issue.startDate instanceof Date && !Number.isNaN(issue.startDate.getTime())
      ? formatDate(issue.startDate, { weekday: "short", month: "short", day: "numeric" })
      : issue.date || t("calendar.duty.thisDate");
    const start = time(issue.startDate);
    const end = time(issue.endDate);
    const range = start && end ? t("calendar.duty.range", { start, end }) : "";
    if (issue.type === "doctor_overlap") {
      const rooms = Array.from(new Set((issue.duties || []).map((duty) => nameOf(duty.roomId, "calendar.duty.anotherRoom"))));
      return t("calendar.duty.doctorOverlap", { doctor, rooms: rooms.join(" · "), date, range });
    }
    const doctors = Array.from(new Set((issue.duties || []).map((duty) => nameOf(duty.doctorId, "calendar.duty.anotherDoctor"))));
    return t("calendar.duty.roomOverlap", { room, doctors: doctors.join(" · "), date, range });
  }
  if (issue.type === "missing_assignment") return t("calendar.duty.missingAssignment");
  if (issue.type === "invalid_time") return t("calendar.duty.invalidTime");
  if (issue.type === "service_not_supported") return t("calendar.duty.serviceUnsupported", { doctor, room });
  if (issue.type === "outside_duty_window") {
    const start = time(issue.duty?.startDate);
    const end = time(issue.duty?.endDate);
    const range = start && end ? t("calendar.duty.chooseRange", { start, end }) : "";
    return t("calendar.duty.outsideWindow", { doctor, room, range });
  }
  if (alternateRoom && issue.coveringDuty.roomId !== issue.roomId) {
    return t("calendar.duty.alternateRoom", { doctor, alternateRoom, room });
  }
  return t("calendar.duty.notOnDuty", { doctor, room });
}

function isLowShowProbability(booking) {
  if (!["hold", "pending", "confirmed"].includes(booking.state)) return false;
  const probability = getAttendanceSignal(booking)?.probability;
  return Number.isFinite(probability) && probability < 75;
}

export default function Calendar() {
  const { state } = useStore();
  const { t, localize, formatDate, formatMoney } = useI18n();
  const [view, setView] = useState("day");
  const [planningLens, setPlanningLens] = useState("doctors");
  const [cursor, setCursor] = useState(() => {
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    if (date.getDay() === 0) date = addDays(date, 1);
    return date;
  });
  const [resourceFilter, setResourceFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [createSeed, setCreateSeed] = useState(null);
  const [riskOpen, setRiskOpen] = useState(false);
  const [appointmentsOpen, setAppointmentsOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const selected = state.bookings.find((booking) => booking.id === selectedId);
  const lensType = planningLens === "doctors" ? "person" : "room";
  const lensResources = state.resources.filter((resource) => resource.type === lensType);
  const visibleResources = resourceFilter === "all"
    ? lensResources
    : lensResources.filter((resource) => resource.id === resourceFilter);

  useEffect(() => {
    setResourceFilter("all");
  }, [planningLens]);

  const periodDays = useMemo(() => {
    if (view === "day") return [cursor];
    if (view === "month") return monthDays(cursor);
    const monday = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
  }, [cursor, view]);

  const periodBookings = useMemo(() => state.bookings.filter((booking) => {
    const inPeriod = periodDays.some((day) => sameDay(booking.start, day));
    const matchesFilter = resourceFilter === "all"
      || bookingMatchesResource(booking, resourceFilter, state.services);
    return inPeriod && matchesFilter;
  }), [periodDays, resourceFilter, state.bookings, state.services]);

  const riskBookings = useMemo(() => periodBookings
    .filter(isLowShowProbability)
    .sort((a, b) => new Date(a.start) - new Date(b.start)), [periodBookings]);

  const scheduleIssues = useMemo(() => {
    const seen = new Set();
    const actionableBookings = periodBookings.filter(isActionableScheduleBooking);
    const bookingOverlaps = actionableBookings.flatMap((booking) => (
      findBookingConflictDetails(actionableBookings, state.services, booking, booking.id)
        .flatMap((detail) => {
          const key = [booking.id, detail.booking.id].sort().join(":");
          if (seen.has(key)) return [];
          seen.add(key);
          return [{ ...detail, kind: "booking_overlap", booking, other: detail.booking }];
        })
    ));
    const dutyBookingIssues = actionableBookings
      .flatMap((booking) => {
        const issue = findDutyAssignmentIssue(state.roomDuties, booking);
        return issue ? [{ ...issue, kind: "booking_duty", booking }] : [];
      });
    const rosterIssues = findDutyRosterConflicts(state.roomDuties, periodDays)
      .map((issue) => ({ ...issue, kind: "duty_roster" }));
    return [...bookingOverlaps, ...dutyBookingIssues, ...rosterIssues];
  }, [periodBookings, periodDays, state.roomDuties, state.services]);

  const metrics = useMemo(() => {
    const scheduled = periodBookings.filter((booking) => activeBooking(booking) && booking.state !== "noshow");
    const scheduledValue = scheduled.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
    const attendedValue = scheduled
      .filter((booking) => booking.state === "completed")
      .reduce((sum, booking) => sum + Number(booking.price || 0), 0);
    const riskValue = riskBookings
      .reduce((sum, booking) => sum + Number(booking.price || 0), 0);
    const workingDays = periodDays.filter((day) => day.getDay() !== 0).length;
    const staffedRoomMinutes = dutyMinutesForRooms(
      state.roomDuties,
      periodDays,
      visibleResources.map((resource) => resource.id),
    );
    const totalMinutes = planningLens === "rooms"
      ? Object.values(staffedRoomMinutes).reduce((sum, minutes) => sum + Number(minutes || 0), 0)
      : workingDays * visibleResources.length * (END - START) * 60;
    const reservedMinutes = periodBookings
      .filter(activeBooking)
      .reduce((sum, booking) => {
        const visibleResourceCount = bookingResourceIds(booking, state.services)
          .filter((id) => visibleResources.some((resource) => resource.id === id)).length;
        return sum + visibleResourceCount * (bookingDurationMinutes(booking) + Number(booking.buffer || 0));
      }, 0);
    const importedOccupiedMinutes = totalMinutes * (planningLens === "rooms" ? 0.61 : 0.58);
    const occupiedMinutes = Math.max(reservedMinutes, importedOccupiedMinutes);
    const openCapacity = totalMinutes > 0
      ? Math.max(0, Math.round((1 - occupiedMinutes / totalMinutes) * 100))
      : 0;
    return {
      scheduledValue,
      attendedValue,
      riskValue,
      openCapacity,
      occupiedCapacity: Math.min(100, Math.round((occupiedMinutes / Math.max(1, totalMinutes)) * 100)),
      closed: workingDays === 0,
      count: periodBookings.length,
      confirmed: periodBookings.filter((booking) => booking.state === "confirmed").length,
    };
  }, [periodBookings, periodDays, planningLens, riskBookings, state.roomDuties, state.services, visibleResources]);

  const move = (direction) => setCursor((current) => {
    if (view === "month") return new Date(current.getFullYear(), current.getMonth() + direction, 1);
    return addDays(current, view === "day" ? direction : direction * 7);
  });

  const openNew = (seed = {}) => {
    const fallback = nextDefaultStart(cursor, state.services);
    const baseDate = seed.date ? new Date(seed.date) : fallback.date;
    const selectedResource = state.resources.find((resource) => resource.id === seed.resourceId);
    setCreateSeed({
      customerId: seed.customerId || "",
      serviceId: seed.serviceId || "",
      resourceId: seed.resourceId || "",
      doctorId: seed.doctorId || (selectedResource?.type === "person" ? selectedResource.id : ""),
      roomId: seed.roomId || (selectedResource?.type === "room" ? selectedResource.id : ""),
      assignmentIntent: seed.assignmentIntent || (selectedResource?.type === "person" ? "doctor" : "service"),
      date: dateInputValue(baseDate),
      time: seed.time || fallback.time,
      rebookedFrom: seed.rebookedFrom || null,
    });
  };

  const rangeLabel = view === "day"
    ? formatDate(cursor, { day: "numeric", month: "long", year: "numeric" })
    : view === "month"
      ? formatDate(cursor, { month: "long", year: "numeric" })
      : `${formatDate(startOfWeek(cursor), { day: "numeric", month: "short" })} – ${formatDate(addDays(startOfWeek(cursor), 6), { day: "numeric", month: "short" })}`;

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-6 lg:px-8 lg:py-8">
      <PageHeader
        title={t("calendar.page.title")}
        subtitle={t("calendar.page.subtitle")}
      />

      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border shadow-xs 2xl:grid-cols-4">
        <MetricCell
          icon={Banknote}
          label={t("calendar.metric.scheduledValue")}
          value={formatMoney(metrics.scheduledValue)}
          sub={t("calendar.metric.attendedValue", { value: formatMoney(metrics.attendedValue) })}
          source="derived"
          primary
        />
        <MetricCell
          icon={CalendarClock}
          label={t("calendar.metric.appointments")}
          value={metrics.count}
          sub={t("calendar.metric.confirmed", { count: metrics.confirmed })}
          source="derived"
          onClick={() => setAppointmentsOpen(true)}
        />
        <MetricCell
          icon={ShieldAlert}
          label={t("calendar.metric.valueAtRisk")}
          value={formatMoney(metrics.riskValue)}
          sub={t("calendar.metric.lowShowRisk")}
          source="estimate"
          onClick={() => setRiskOpen(true)}
        />
        <MetricCell
          icon={Gauge}
          label={t("calendar.metric.bookableCapacity")}
          value={metrics.closed ? t("calendar.metric.closed") : `${metrics.openCapacity}%`}
          sub={metrics.closed
            ? t("calendar.metric.closedSunday")
            : t("calendar.metric.occupied", { value: metrics.occupiedCapacity })}
          source="derived"
        />
      </div>

      <RiskBookingsDialog
        open={riskOpen}
        onOpenChange={setRiskOpen}
        bookings={riskBookings}
        state={state}
        rangeLabel={rangeLabel}
        onSelect={(bookingId) => {
          setRiskOpen(false);
          setSelectedId(bookingId);
        }}
      />

      <AppointmentsDialog
        open={appointmentsOpen}
        onOpenChange={setAppointmentsOpen}
        bookings={periodBookings}
        state={state}
        rangeLabel={rangeLabel}
        onSelect={(bookingId) => {
          setAppointmentsOpen(false);
          setSelectedId(bookingId);
        }}
      />

      <ScheduleIssuesDialog
        open={issuesOpen}
        onOpenChange={setIssuesOpen}
        issues={scheduleIssues}
        state={state}
        onSelect={(bookingId) => {
          setIssuesOpen(false);
          setSelectedId(bookingId);
        }}
      />

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <CalendarSurfaceHeader
          view={view}
          planningLens={planningLens}
          issueCount={scheduleIssues.length}
          cursor={cursor}
          rangeLabel={rangeLabel}
          resources={lensResources}
          resourceFilter={resourceFilter}
          onResourceChange={setResourceFilter}
          onViewChange={setView}
          onPlanningLensChange={setPlanningLens}
          onIssuesOpen={() => setIssuesOpen(true)}
          onMove={move}
          onToday={() => {
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            setCursor(date);
          }}
          onCreate={() => openNew()}
        />

        {view === "day" ? (
          <DayGrid
            state={state}
            cursor={cursor}
            resources={visibleResources}
            planningLens={planningLens}
            onSelect={setSelectedId}
            onCreate={openNew}
          />
        ) : view === "week" ? (
          <WeekGrid
            state={state}
            cursor={cursor}
            resourceFilter={resourceFilter}
            planningLens={planningLens}
            onSelect={setSelectedId}
            onCreate={openNew}
          />
        ) : (
          <MonthGrid
            state={state}
            cursor={cursor}
            resourceFilter={resourceFilter}
            onSelect={setSelectedId}
            onCreate={openNew}
          />
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
        {Object.keys(BOOKING_STATE).map((key) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className={`inline-block h-3 w-3 rounded-sm border ${STATE_STYLE[key]?.split(" ").slice(0, 2).join(" ")}`} />
            {localize(BOOKING_STATE[key].label)}
          </span>
        ))}
        <span className="ml-auto hidden text-[11px] sm:inline">{t("calendar.legend.addSlotHint")}</span>
      </div>

      <BookingDrawer
        booking={selected}
        onClose={() => setSelectedId(null)}
        onRebook={(booking) => {
          setSelectedId(null);
          openNew({
            customerId: booking.customerId,
            serviceId: booking.serviceId,
            resourceId: booking.resourceId,
            doctorId: booking.doctorId,
            roomId: booking.roomId,
            date: addDays(new Date(), 1),
            time: timeInputValue(booking.start),
            rebookedFrom: booking.id,
          });
        }}
      />

      {createSeed ? (
        <NewBookingSheet
          seed={createSeed}
          onClose={() => setCreateSeed(null)}
          onCreated={(id) => setSelectedId(id)}
        />
      ) : null}
    </div>
  );
}

function CalendarSurfaceHeader({
  view,
  planningLens,
  issueCount,
  cursor,
  rangeLabel,
  resources,
  resourceFilter,
  onResourceChange,
  onViewChange,
  onPlanningLensChange,
  onIssuesOpen,
  onMove,
  onToday,
  onCreate,
}) {
  const { t, localize, formatDate } = useI18n();
  const referenceDate = view === "week" ? startOfWeek(cursor) : cursor;
  const title = view === "day"
    ? formatDate(cursor, { day: "numeric", month: "long", year: "numeric" })
    : formatDate(cursor, { month: "long", year: "numeric" });
  const support = view === "day"
    ? formatDate(cursor, { weekday: "long" })
    : rangeLabel;

  return (
    <div className="border-b border-border px-4 py-4 sm:px-6 sm:py-5">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
          <div className="hidden h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-background shadow-xs sm:block">
            <div className="border-b border-border bg-muted/60 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {formatDate(referenceDate, { month: "short" })}
            </div>
            <div className="flex h-10 items-center justify-center text-lg font-semibold tabular-nums text-foreground">
              {referenceDate.getDate()}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold leading-7 tracking-tight text-foreground">{title}</h2>
              {view === "week" ? (
                <span className="rounded-full border border-border bg-muted/55 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {t("calendar.header.weekNumber", { number: isoWeekNumber(cursor) })}
                </span>
              ) : null}
            </div>
            <p className="truncate text-sm leading-5 text-muted-foreground">{support}</p>
          </div>
          </div>
          <Button className="h-9 shrink-0 whitespace-nowrap rounded-lg px-3" onClick={onCreate}>
            <Plus className="mr-1.5 h-4 w-4" /> {t("calendar.header.newBooking")}
          </Button>
        </div>

        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-9 items-center rounded-lg border border-input bg-background p-0.5 shadow-xs" aria-label={t("calendar.header.planningLens")}>
            <button
              type="button"
              aria-pressed={planningLens === "doctors"}
              onClick={() => onPlanningLensChange("doctors")}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                planningLens === "doctors"
                  ? "bg-primary-soft text-primary-soft-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Users className="h-4 w-4" /> {t("calendar.header.doctors")}
            </button>
            <button
              type="button"
              aria-pressed={planningLens === "rooms"}
              onClick={() => onPlanningLensChange("rooms")}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                planningLens === "rooms"
                  ? "bg-primary-soft text-primary-soft-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Building2 className="h-4 w-4" /> {t("calendar.header.rooms")}
            </button>
            </div>

            <button
              type="button"
              onClick={onIssuesOpen}
              className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-semibold shadow-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                issueCount
                  ? "border-destructive/35 bg-destructive-soft text-destructive-soft-foreground hover:bg-destructive-soft/75"
                  : "border-input bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {issueCount ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              {issueCount
                ? t(issueCount === 1 ? "calendar.header.conflict" : "calendar.header.conflicts", { count: issueCount })
                : t("calendar.header.scheduleClear")}
            </button>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <select
              id="calendar-resource"
              aria-label={t("calendar.header.filterResource")}
              value={resourceFilter}
              onChange={(event) => onResourceChange(event.target.value)}
              className="h-9 min-w-0 rounded-lg border border-input bg-background px-3 text-sm font-medium text-foreground shadow-xs outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/20 sm:w-44"
            >
              <option
                value="all"
                label={planningLens === "doctors" ? t("calendar.header.allDoctors") : t("calendar.header.allRooms")}
              />
              {resources.map((resource) => (
                <option key={resource.id} value={resource.id} label={localize(resource.short)} />
              ))}
            </select>

            <div className="inline-flex h-9 overflow-hidden rounded-lg border border-input bg-background shadow-xs">
            <button
              type="button"
              aria-label={t("calendar.header.previousDate")}
              onClick={() => onMove(-1)}
              className="inline-flex w-9 items-center justify-center text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onToday}
              className="border-x border-input px-3 text-sm font-semibold text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              {t("calendar.header.today")}
            </button>
            <button
              type="button"
              aria-label={t("calendar.header.nextDate")}
              onClick={() => onMove(1)}
              className="inline-flex w-9 items-center justify-center text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            </div>

            <div className="inline-flex h-9 items-center rounded-lg bg-muted/70 p-0.5">
            {["day", "week", "month"].map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={view === item}
                onClick={() => onViewChange(item)}
                className={`h-8 rounded-md px-3 text-sm font-semibold capitalize outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                  view === item
                    ? "border border-input bg-background text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t(`calendar.header.${item}`)}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCell({ icon: Icon, label, value, sub, source, primary = false, onClick = null }) {
  const { t } = useI18n();
  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className={`flex min-w-0 items-center gap-2 text-xs font-medium leading-[18px] sm:text-sm sm:leading-5 ${primary ? "text-primary-soft-foreground" : "text-muted-foreground"}`}>
          <Icon className="h-4 w-4 shrink-0" /> {label}
        </p>
        <SourceTag
          source={source}
          className={source === "estimate" ? "hidden sm:inline-flex" : "hidden 2xl:inline-flex"}
        />
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-foreground lg:text-[30px] lg:leading-9">{value}</p>
      <p className="mt-1 text-xs leading-[18px] text-muted-foreground">{sub}</p>
    </>
  );

  const className = `min-w-0 p-4 text-left lg:p-5 ${primary ? "bg-primary-soft/80" : "bg-surface"}`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${className} w-full outline-none transition-colors hover:bg-warning-soft/20 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring`}
        aria-label={t("calendar.metric.viewBookings", { label, value, details: sub })}
      >
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}

function RiskBookingsDialog({ open, onOpenChange, bookings, state, rangeLabel, onSelect }) {
  const { t, localize, formatDate, formatMoney } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b border-border px-6 py-5 text-left">
          <DialogTitle>{t("calendar.risk.title")}</DialogTitle>
          <DialogDescription>
            {t("calendar.risk.description", { range: rangeLabel })}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[460px] overflow-y-auto p-3 scroll-thin">
          {bookings.length ? (
            <div className="space-y-2">
              {bookings.map((booking) => {
                const customer = state.customers.find((item) => item.id === booking.customerId);
                const service = state.services.find((item) => item.id === booking.serviceId);
                const attendance = getAttendanceSignal(booking);
                const critical = Number(attendance?.probability) < 60;
                return (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => onSelect(booking.id)}
                    className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface p-3 text-left outline-none transition-colors hover:bg-muted/45 focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${critical ? "bg-destructive-soft text-destructive-soft-foreground" : "bg-warning-soft text-warning-soft-foreground"}`}>
                      <AlertTriangle className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {t("calendar.risk.atTime", {
                          service: localize(service?.name),
                          date: formatDate(booking.start, { day: "numeric", month: "short" }),
                          time: fmtTime(booking.start),
                        })}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums text-foreground">{formatMoney(booking.price)}</span>
                      <span className={`mt-0.5 block text-xs font-semibold ${critical ? "text-destructive-soft-foreground" : "text-warning-soft-foreground"}`}>
                        {localize(attendance?.shortLabel)}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success-soft-foreground">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-semibold text-foreground">{t("calendar.risk.emptyTitle")}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("calendar.risk.emptyBody")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AppointmentsDialog({ open, onOpenChange, bookings, state, rangeLabel, onSelect }) {
  const { t, localize, formatDate, formatMoney } = useI18n();
  const sorted = [...bookings].sort((a, b) => new Date(a.start) - new Date(b.start));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border px-6 py-5 text-left">
          <DialogTitle>{t("calendar.appointments.title")}</DialogTitle>
          <DialogDescription>{t("calendar.appointments.description", { range: rangeLabel, count: sorted.length })}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[520px] overflow-y-auto p-3 scroll-thin">
          {sorted.length ? (
            <div className="space-y-2">
              {sorted.map((booking) => {
                const customer = state.customers.find((item) => item.id === booking.customerId);
                const service = state.services.find((item) => item.id === booking.serviceId);
                const assignments = bookingAssignments(booking, state.services, state.resources);
                const doctor = state.resources.find((item) => item.id === assignments.doctorId);
                const room = state.resources.find((item) => item.id === assignments.roomId);
                return (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => onSelect(booking.id)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-lg border border-border bg-surface p-3 text-left outline-none transition-colors hover:bg-muted/45 focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[100px_minmax(0,1fr)_auto]"
                  >
                    <span className="text-xs font-semibold tabular-nums text-foreground">
                      {formatDate(booking.start, { day: "numeric", month: "short" })}
                      <span className="mt-0.5 block font-medium text-muted-foreground">{fmtTime(booking.start)}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{localize(service?.name)}</span>
                      <span className="mt-1 block truncate text-[11px] text-muted-foreground">{t("calendar.appointments.assignment", {
                        doctor: localize(doctor?.short) || t("calendar.appointments.doctorUnassigned"),
                        room: localize(room?.short) || t("calendar.appointments.roomUnassigned"),
                      })}</span>
                    </span>
                    <span className="col-start-2 row-start-1 shrink-0 text-right sm:col-start-3">
                      <span className="block text-sm font-semibold tabular-nums text-foreground">{formatMoney(booking.price)}</span>
                      <span className="mt-1 inline-flex"><BookingStateBadge state={booking.state} /></span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <CalendarClock className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-3 text-sm font-semibold text-foreground">{t("calendar.appointments.emptyTitle")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("calendar.appointments.emptyBody")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleIssuesDialog({ open, onOpenChange, issues, state, onSelect }) {
  const { t, localize, formatDate } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b border-border px-6 py-5 text-left">
          <DialogTitle>{t("calendar.conflicts.title")}</DialogTitle>
          <DialogDescription>
            {t("calendar.conflicts.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[460px] overflow-y-auto p-3 scroll-thin">
          {issues.length ? (
            <div className="space-y-2">
              {issues.map((issue, index) => {
                const booking = issue.booking;
                const customer = booking
                  ? state.customers.find((item) => item.id === booking.customerId)
                  : null;
                const otherCustomer = issue.other
                  ? state.customers.find((item) => item.id === issue.other.customerId)
                  : null;
                const shared = (issue.sharedResourceIds || [])
                  .map((id) => state.resources.find((resource) => resource.id === id)?.short)
                  .filter(Boolean)
                  .join(" + ");
                const title = issue.kind === "booking_overlap"
                  ? localize(shared) || t("calendar.conflicts.sharedResource")
                  : issue.kind === "booking_duty"
                    ? t("calendar.conflicts.bookingOutsideDuty", { customer: localize(customer?.name) || t("calendar.conflicts.bookingFallback") })
                    : t("calendar.conflicts.dutyOverlap");
                const baseDescription = issue.kind === "booking_overlap"
                  ? t(issue.type === "buffer" ? "calendar.conflicts.bufferOverlap" : "calendar.conflicts.appointmentOverlap", {
                      customer: localize(customer?.name),
                      time: fmtTime(booking.start),
                      other: otherCustomer?.name,
                      otherTime: fmtTime(issue.other.start),
                    })
                  : localizedDutyIssueMessage(issue, state.resources, t, localize, formatDate);
                const description = issue.kind === "duty_roster" && issue.occurrenceCount > 1
                  ? t("calendar.conflicts.repeats", { description: baseDescription, count: issue.occurrenceCount })
                  : baseDescription;
                return (
                  <button
                    key={`${issue.kind}-${booking?.id || issue.id || index}`}
                    type="button"
                    disabled={!booking}
                    onClick={() => booking && onSelect(booking.id)}
                    className="flex w-full items-start gap-3 rounded-lg border border-destructive/25 bg-destructive-soft/45 p-3 text-left outline-none transition-colors enabled:hover:bg-destructive-soft/70 enabled:focus-visible:ring-2 enabled:focus-visible:ring-ring disabled:cursor-default"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-foreground">{title}</span>
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                        {description}
                      </span>
                    </span>
                    {booking ? <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success-soft-foreground">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-semibold text-foreground">{t("calendar.conflicts.emptyTitle")}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("calendar.conflicts.emptyBody")}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DayGrid({ state, cursor, resources, planningLens, onSelect, onCreate }) {
  const { t, localize, formatMoney } = useI18n();
  const dayBookings = state.bookings
    .filter((booking) => sameDay(booking.start, cursor))
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const conflictIds = new Set();
  dayBookings.forEach((booking) => {
    if (!isActionableScheduleBooking(booking)) return;
    if (findBookingConflicts(dayBookings, state.services, booking, booking.id).length
      || findDutyAssignmentIssue(state.roomDuties, booking)) {
      conflictIds.add(booking.id);
    }
  });

  const hours = Array.from({ length: END - START + 1 }, (_, index) => START + index);
  const slots = Array.from(
    { length: ((END - START) * 60) / SLOT_MINUTES },
    (_, index) => START + (index * SLOT_MINUTES) / 60,
  );
  const totalHeight = (END - START) * HOUR;
  const now = new Date();
  const showNow = sameDay(cursor, now) && decimalHour(now.toISOString()) >= START && decimalHour(now.toISOString()) <= END;

  if (resources.length === 0) {
    return (
      <div className="border-t border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground">
        {t("calendar.day.noResources")}
      </div>
    );
  }

  if (cursor.getDay() === 0) {
    return (
      <div className="border-t border-border bg-surface px-6 py-20 text-center">
        <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted/45 text-muted-foreground">
          <CalendarClock className="h-5 w-5" aria-hidden="true" />
        </span>
        <p className="mt-4 text-sm font-semibold text-foreground">{t("calendar.day.closedTitle")}</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
          {t("calendar.day.closedBody")}
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[calc(100dvh-300px)] min-h-[520px] overflow-auto bg-surface scroll-thin">
      <div className="min-w-[800px]">
        <div
          className="sticky top-0 z-40 grid border-b border-border bg-surface/95 backdrop-blur"
          style={{ gridTemplateColumns: `72px repeat(${resources.length}, minmax(210px, 1fr))` }}
        >
          <div className="sticky left-0 z-50 bg-surface" />
          {resources.map((resource) => {
            const resourceBookings = dayBookings.filter((booking) => (
              bookingMatchesResource(booking, resource.id, state.services) && activeBooking(booking)
            ));
            const resourceDuties = normalizedResourceDuties(state.roomDuties, cursor, resource.id, planningLens);
            const uncoveredActionableBooking = resourceBookings.some((booking) => (
              isActionableScheduleBooking(booking)
              && Boolean(findDutyAssignmentIssue(state.roomDuties, booking))
            ));
            const value = resourceBookings
              .filter((booking) => booking.state !== "noshow")
              .reduce((sum, booking) => sum + Number(booking.price || 0), 0);
            return (
              <div key={resource.id} className="border-l border-border px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold leading-5 text-foreground">{localize(resource.short)}</p>
                    <p className="truncate text-xs leading-[18px] text-muted-foreground">{localize(resource.role)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold tabular-nums text-primary-soft-foreground">{formatMoney(value)}</p>
                    <p className="text-[10px] leading-[14px] text-muted-foreground">{t("calendar.day.scheduled")}</p>
                  </div>
                </div>
                {resourceDuties.length ? (
                  <p className="mt-2 flex min-w-0 items-center gap-1.5 truncate text-[11px] font-medium text-primary-soft-foreground">
                    <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {resourceDuties.map((duty) => {
                      const partner = state.resources.find((item) => item.id === (planningLens === "rooms" ? duty.doctorId : duty.roomId));
                      return t("calendar.day.dutyBlock", {
                        resource: localize(partner?.short) || t(planningLens === "rooms" ? "calendar.day.doctor" : "calendar.day.room"),
                        start: fmtTime(duty.startDate),
                        end: fmtTime(duty.endDate),
                      });
                    }).join(" | ")}
                  </p>
                ) : (
                  <p className={`mt-2 text-[11px] font-medium ${uncoveredActionableBooking ? "text-warning-soft-foreground" : "text-muted-foreground"}`}>
                    {t(uncoveredActionableBooking ? "calendar.day.dutyNeedsReview" : "calendar.day.notScheduled")}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div
          className="grid"
          data-slot-grid
          style={{ gridTemplateColumns: `72px repeat(${resources.length}, minmax(210px, 1fr))` }}
        >
          <div className="sticky left-0 z-30 bg-surface" style={{ height: totalHeight }}>
            {hours.map((hour, index) => (
              <div
                key={hour}
                className="absolute left-0 right-0 -translate-y-1/2 pr-3 text-right text-xs tabular-nums text-muted-foreground"
                style={{ top: index * HOUR }}
              >
                {String(hour).padStart(2, "0")}:00
              </div>
            ))}
            {showNow ? (
              <span
                className="absolute right-0 h-2.5 w-2.5 translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-accent"
                style={{ top: (decimalHour(now.toISOString()) - START) * HOUR }}
              />
            ) : null}
          </div>

          {resources.map((resource, resourceIndex) => {
            const resourceBookings = dayBookings.filter((booking) => (
              bookingMatchesResource(booking, resource.id, state.services)
            ));
            const resourceDuties = normalizedResourceDuties(state.roomDuties, cursor, resource.id, planningLens);
            const firstDutySlotIndex = Math.max(0, slots.findIndex((hour) => dutyCoveringResourceHour(state.roomDuties, cursor, resource.id, planningLens, hour)));
            return (
              <div
                key={resource.id}
                className="relative border-l border-border bg-muted/10"
                style={{ height: totalHeight }}
              >
                {hours.map((hour, index) => (
                  <React.Fragment key={hour}>
                    <div className="pointer-events-none absolute left-0 right-0 border-t border-border/70" style={{ top: index * HOUR }} />
                    {index < hours.length - 1 ? (
                      <div className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-border/35" style={{ top: index * HOUR + HOUR / 2 }} />
                    ) : null}
                  </React.Fragment>
                ))}

                {resourceDuties.map((duty) => {
                  const partner = state.resources.find((item) => item.id === (planningLens === "rooms" ? duty.doctorId : duty.roomId));
                  const startHour = decimalHour(duty.startDate.toISOString());
                  const endHour = decimalHour(duty.endDate.toISOString());
                  const top = Math.max(0, (startHour - START) * HOUR);
                  const height = Math.max(0, (Math.min(END, endHour) - Math.max(START, startHour)) * HOUR);
                  if (!height) return null;
                  return (
                    <div
                      key={duty.id}
                      className="pointer-events-none absolute inset-x-1 z-0 overflow-hidden rounded-md border border-primary/25 border-l-[3px] border-l-primary/65 bg-primary-soft/55"
                      style={{ top, height }}
                    >
                      <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] font-semibold text-primary-soft-foreground">
                        <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span className="truncate">{t(planningLens === "rooms" ? "calendar.day.doctor" : "calendar.day.room")}: {localize(partner?.short) || t("calendar.day.notAssigned")}</span>
                        <span className="ml-auto shrink-0 font-medium tabular-nums opacity-75">
                          {fmtTime(duty.startDate)}-{fmtTime(duty.endDate)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {slots.map((hour, slotIndex) => {
                  const duty = dutyCoveringResourceHour(state.roomDuties, cursor, resource.id, planningLens, hour);
                  const disabled = !duty;
                  const partner = duty
                    ? state.resources.find((item) => item.id === (planningLens === "rooms" ? duty.doctorId : duty.roomId))
                    : null;
                  return (
                    <button
                    key={hour}
                    type="button"
                    disabled={disabled}
                    tabIndex={slotIndex === firstDutySlotIndex ? 0 : -1}
                    data-slot-column={resourceIndex}
                    data-slot-index={slotIndex}
                    aria-label={disabled
                      ? t("calendar.day.unavailableSlot", { resource: localize(resource.short), time: slotTime(hour) })
                      : t("calendar.day.addSlot", { resource: localize(resource.short), time: slotTime(hour) })}
                    onClick={() => onCreate({
                      date: cursor,
                      time: slotTime(hour),
                      resourceId: resource.id,
                      roomId: duty?.roomId || (planningLens === "rooms" ? resource.id : ""),
                      doctorId: duty?.doctorId || (planningLens === "doctors" ? resource.id : ""),
                    })}
                    onKeyDown={(event) => moveSlotFocus(
                      event,
                      resourceIndex,
                      slotIndex,
                      resources.length,
                      slots.length,
                    )}
                    className="group absolute inset-x-0 z-[1] flex items-center justify-center text-primary opacity-0 outline-none transition-colors hover:bg-primary-soft/55 hover:opacity-100 focus-visible:bg-primary-soft/65 focus-visible:opacity-100 disabled:pointer-events-none"
                    style={{ top: (hour - START) * HOUR, height: (SLOT_MINUTES / 60) * HOUR }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  );
                })}

                {showNow ? (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-20 border-t border-accent/80"
                    style={{ top: (decimalHour(now.toISOString()) - START) * HOUR }}
                  />
                ) : null}

                {resourceBookings.map((booking) => (
                  <CalendarEvent
                    key={`${resource.id}-${booking.id}`}
                    state={state}
                    booking={booking}
                    conflict={conflictIds.has(booking.id)}
                    planningLens={planningLens}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CalendarEvent({ state, booking, conflict, planningLens, onSelect, layout = null }) {
  const { t, localize, formatMoney } = useI18n();
  const service = state.services.find((item) => item.id === booking.serviceId);
  const customer = state.customers.find((item) => item.id === booking.customerId);
  const assignments = bookingAssignments(booking, state.services, state.resources);
  const doctor = state.resources.find((resource) => resource.id === assignments.doctorId);
  const room = state.resources.find((resource) => resource.id === assignments.roomId);
  const planningContext = planningLens === "rooms" ? doctor?.short : room?.short;
  const top = (decimalHour(booking.start) - START) * HOUR;
  const duration = bookingDurationMinutes(booking);
  const eventHeight = Math.max((duration / 60) * HOUR, 24);
  const bufferHeight = (Number(booking.buffer || 0) / 60) * HOUR;
  const compact = duration < 30;
  const stateLabel = localize(BOOKING_STATE[booking.state]?.label || booking.state);
  const attendance = getAttendanceSignal(booking);
  const attendanceLabel = Number.isFinite(attendance?.probability)
    ? t("calendar.event.showProbability", { value: attendance.probability })
    : localize(attendance?.label);
  const warningEntry = ["warning", "destructive"].includes(attendance?.tone)
    || ["hold", "pending", "noshow"].includes(booking.state);
  const horizontalStyle = layout
    ? {
        left: `calc(${(layout.lane / layout.laneCount) * 100}% + 6px)`,
        width: `calc(${100 / layout.laneCount}% - 10px)`,
      }
    : { left: 6, right: 6 };

  if (top < 0 || top >= (END - START) * HOUR) return null;

  return (
    <div data-booking="true" className="absolute z-10" style={{ top, ...horizontalStyle }}>
      <HoverCard openDelay={250} closeDelay={100}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            onClick={() => onSelect(booking.id)}
            aria-label={t("calendar.event.accessible", {
              time: fmtTime(booking.start),
              customer: localize(customer?.name),
              service: localize(service?.name),
              doctor: localize(doctor?.short) || t("calendar.event.doctorUnassigned"),
              room: localize(room?.short) || t("calendar.event.roomUnassigned"),
              value: formatMoney(booking.price),
              status: stateLabel,
              attendance: attendanceLabel,
            })}
            className={`w-full overflow-hidden rounded-md border text-left outline-none transition-colors hover:border-primary/70 focus-visible:ring-2 focus-visible:ring-ring ${
              compact ? "px-1.5 py-0.5" : "px-2 py-1"
            } ${STATE_STYLE[booking.state]} ${conflict ? "ring-2 ring-destructive" : ""}`}
            style={{ height: eventHeight }}
          >
            {compact ? (
              <div className="flex min-w-0 items-center gap-1.5 text-[10px] leading-none">
                {warningEntry ? <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
                <span className="shrink-0 font-semibold tabular-nums">{fmtTime(booking.start)}</span>
                <span className="min-w-0 flex-1 truncate font-semibold">{localize(customer?.name)}</span>
                <span className="shrink-0 font-semibold opacity-80">{Number.isFinite(attendance?.probability) ? `${attendance.probability}%` : localize(attendance?.shortLabel)}</span>
                <span className="shrink-0 font-semibold tabular-nums">{formatMoney(booking.price)}</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 text-[11px] leading-[14px]">
                  <span className="flex min-w-0 items-center gap-1 font-semibold tabular-nums">
                    {warningEntry ? <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
                    {fmtTime(booking.start)}
                  </span>
                  <span className="font-semibold tabular-nums">{formatMoney(booking.price)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 truncate text-xs font-semibold leading-4">{localize(customer?.name)}</p>
                  <p className="shrink-0 text-[10px] font-semibold leading-[14px] opacity-80">{Number.isFinite(attendance?.probability) ? `${attendance.probability}%` : localize(attendance?.shortLabel)}</p>
                </div>
                {duration >= 30 && planningContext ? (
                  <p className="truncate text-[10px] font-medium leading-[14px] opacity-75">
                    {t(planningLens === "rooms" ? "calendar.day.doctor" : "calendar.day.room")}: {localize(planningContext)}
                  </p>
                ) : null}
              </>
            )}
          </button>
        </HoverCardTrigger>
        <HoverCardContent align="start" className="w-72 p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">{localize(customer?.name)}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{localize(service?.name)}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-base font-semibold tabular-nums text-primary-soft-foreground">{formatMoney(booking.price)}</p>
              <p className="text-[10px] text-muted-foreground">{t("calendar.event.bookedValue")}</p>
            </div>
          </div>
          <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-xs">
            <Line label={t("calendar.event.when")} value={t("calendar.event.timeRange", { start: fmtTime(booking.start), end: fmtTime(booking.end) })} />
            <Line label={t("calendar.day.doctor")} value={localize(doctor?.name) || t("calendar.day.notAssigned")} />
            <Line label={t("calendar.day.room")} value={localize(room?.name) || t("calendar.day.notAssigned")} />
            <Line label={t("calendar.event.timeHeld")} value={t("calendar.event.durationBuffer", { duration, buffer: booking.buffer })} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <BookingStateBadge state={booking.state} />
            <AttendanceBadge booking={booking} />
            <RiskBadge risk={booking.risk} source={booking.source} />
            {conflict ? <Chip tone="destructive">{t("calendar.event.resourceConflict")}</Chip> : null}
          </div>
        </HoverCardContent>
      </HoverCard>
      {booking.state !== "cancelled" && bufferHeight > 4 ? (
        <div
          className="mx-0.5 rounded-b-md border-x border-b border-dashed border-border bg-muted/45"
          style={{ height: bufferHeight }}
          title={t("calendar.event.buffer", { minutes: booking.buffer })}
        />
      ) : null}
    </div>
  );
}

function WeekGrid({ state, cursor, resourceFilter, planningLens, onSelect, onCreate }) {
  const { t, localize, formatDate, formatMoney } = useI18n();
  const monday = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, index) => addDays(monday, index));
  const allWeekBookings = state.bookings
    .filter((booking) => days.some((day) => sameDay(booking.start, day)))
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const conflictIds = new Set();
  allWeekBookings.forEach((booking) => {
    if (!isActionableScheduleBooking(booking)) return;
    if (findBookingConflicts(allWeekBookings, state.services, booking, booking.id).length
      || findDutyAssignmentIssue(state.roomDuties, booking)) {
      conflictIds.add(booking.id);
    }
  });
  const hours = Array.from({ length: END - START + 1 }, (_, index) => START + index);
  const slots = Array.from(
    { length: ((END - START) * 60) / SLOT_MINUTES },
    (_, index) => START + (index * SLOT_MINUTES) / 60,
  );
  const totalHeight = (END - START) * HOUR;
  const now = new Date();
  const nowHour = decimalHour(now.toISOString());
  const showNow = nowHour >= START && nowHour <= END;
  const dayLayouts = days.map((day) => {
    const list = allWeekBookings
      .filter((booking) => sameDay(booking.start, day))
      .filter((booking) => (
        resourceFilter === "all" || bookingMatchesResource(booking, resourceFilter, state.services)
      ));
    const laidOut = layoutWeekBookings(list);
    const laneCount = laidOut.reduce((highest, item) => Math.max(highest, item.laneCount), 1);
    const active = list.filter((booking) => activeBooking(booking) && booking.state !== "noshow");
    const dayValue = active.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
    const duties = resourceFilter !== "all"
      ? normalizedResourceDuties(state.roomDuties, day, resourceFilter, planningLens)
      : [];
    return {
      day,
      list,
      laidOut,
      laneCount,
      activeCount: active.length,
      dayValue,
      duties,
      isToday: sameDay(day, now),
      closed: day.getDay() === 0,
    };
  });
  const gridTemplate = `72px ${dayLayouts
    .map(({ laneCount }) => `minmax(${laneCount * 160}px, ${laneCount}fr)`)
    .join(" ")}`;

  return (
    <div className="max-h-[calc(100dvh-300px)] min-h-[520px] overflow-auto bg-surface scroll-thin">
      <div className="min-w-[1192px]">
        <div
          className="sticky top-0 z-40 grid border-b border-border bg-surface/95 backdrop-blur"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="sticky left-0 z-50 flex items-end justify-center bg-surface pb-3 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("calendar.week.time")}
          </div>
          {dayLayouts.map(({ day, activeCount, dayValue, duties, isToday, closed }) => {
            return (
              <div
                key={day.toISOString()}
                className={`border-l border-border px-3 py-2.5 ${isToday ? "bg-primary-soft/45" : "bg-surface"} ${closed ? "bg-muted/50" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-xs font-medium leading-[18px] ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                      {formatDate(day, { weekday: "short" })}
                    </p>
                    <p className="text-sm font-semibold leading-5 tabular-nums text-foreground">
                      {formatDate(day, { day: "numeric", month: "short" })}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-semibold tabular-nums text-primary-soft-foreground">{formatMoney(dayValue)}</p>
                    <p className="text-[10px] leading-[14px] text-muted-foreground">
                      {closed ? t("calendar.week.closed") : t("calendar.week.booked", { count: activeCount })}
                    </p>
                  </div>
                </div>
                {duties.length ? (
                  <p className="mt-1 truncate text-[10px] font-medium text-primary-soft-foreground">
                    {duties.map((duty) => {
                      const partner = state.resources.find((item) => item.id === (planningLens === "rooms" ? duty.doctorId : duty.roomId));
                      return t("calendar.day.dutyBlock", {
                        resource: localize(partner?.short) || t(planningLens === "rooms" ? "calendar.day.doctor" : "calendar.day.room"),
                        start: fmtTime(duty.startDate),
                        end: fmtTime(duty.endDate),
                      });
                    }).join(" | ")}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        <div
          className="grid"
          data-slot-grid
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="sticky left-0 z-30 bg-surface" style={{ height: totalHeight }}>
            {hours.map((hour, index) => (
              <div
                key={hour}
                className="absolute left-0 right-0 -translate-y-1/2 pr-3 text-right text-xs tabular-nums text-muted-foreground"
                style={{ top: index * HOUR }}
              >
                {String(hour).padStart(2, "0")}:00
              </div>
            ))}
            {showNow && days.some((day) => sameDay(day, now)) ? (
              <span
                className="absolute right-0 h-2.5 w-2.5 translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-accent"
                style={{ top: (nowHour - START) * HOUR }}
              />
            ) : null}
          </div>

          {dayLayouts.map(({ day, closed, laidOut, duties, isToday }, dayIndex) => {
            return (
              <div
                key={day.toISOString()}
                className={`relative border-l border-border ${closed ? "bg-muted/35" : "bg-surface"}`}
                style={{ height: totalHeight }}
              >
                {hours.map((hour, index) => (
                  <React.Fragment key={hour}>
                    <div className="pointer-events-none absolute inset-x-0 border-t border-border/70" style={{ top: index * HOUR }} />
                    {index < hours.length - 1 ? (
                      <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-border/35" style={{ top: index * HOUR + HOUR / 2 }} />
                    ) : null}
                  </React.Fragment>
                ))}

                {duties.map((duty) => {
                  const partner = state.resources.find((item) => item.id === (planningLens === "rooms" ? duty.doctorId : duty.roomId));
                  const startHour = decimalHour(duty.startDate.toISOString());
                  const endHour = decimalHour(duty.endDate.toISOString());
                  const top = Math.max(0, (startHour - START) * HOUR);
                  const height = Math.max(0, (Math.min(END, endHour) - Math.max(START, startHour)) * HOUR);
                  if (!height) return null;
                  return (
                    <div
                      key={duty.id}
                      className="pointer-events-none absolute inset-x-1 z-0 overflow-hidden rounded-md border border-primary/25 border-l-[3px] border-l-primary/65 bg-primary-soft/55"
                      style={{ top, height }}
                    >
                      <div className="flex items-center gap-1 px-1.5 py-1 text-[9px] font-semibold text-primary-soft-foreground">
                        <UserCheck className="h-3 w-3 shrink-0" aria-hidden="true" />
                        <span className="truncate">{t(planningLens === "rooms" ? "calendar.day.doctor" : "calendar.day.room")}: {localize(partner?.short) || t("calendar.day.notAssigned")}</span>
                      </div>
                    </div>
                  );
                })}

                {!closed ? slots.map((hour, slotIndex) => {
                  const duty = resourceFilter !== "all"
                    ? dutyCoveringResourceHour(state.roomDuties, day, resourceFilter, planningLens, hour)
                    : null;
                  const disabled = resourceFilter !== "all" && !duty;
                  return (
                    <button
                    key={hour}
                    type="button"
                    disabled={disabled}
                    tabIndex={slotIndex === 0 ? 0 : -1}
                    data-slot-column={dayIndex}
                    data-slot-index={slotIndex}
                    aria-label={t("calendar.week.addSlot", {
                      date: formatDate(day, { day: "numeric", month: "short" }),
                      time: slotTime(hour),
                    })}
                    onClick={() => onCreate({
                      date: day,
                      time: slotTime(hour),
                      resourceId: resourceFilter === "all" ? "" : resourceFilter,
                      roomId: duty?.roomId || (planningLens === "rooms" && resourceFilter !== "all" ? resourceFilter : ""),
                      doctorId: duty?.doctorId || (planningLens === "doctors" && resourceFilter !== "all" ? resourceFilter : ""),
                    })}
                    onKeyDown={(event) => moveSlotFocus(
                      event,
                      dayIndex,
                      slotIndex,
                      dayLayouts.length,
                      slots.length,
                    )}
                    className="absolute inset-x-0 z-[1] flex items-center justify-center text-primary opacity-0 outline-none transition-colors hover:bg-primary-soft/55 hover:opacity-100 focus-visible:bg-primary-soft/65 focus-visible:opacity-100 disabled:pointer-events-none"
                    style={{ top: (hour - START) * HOUR, height: (SLOT_MINUTES / 60) * HOUR }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  );
                }) : null}

                {isToday && showNow ? (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-20 border-t border-accent/80"
                    style={{ top: (nowHour - START) * HOUR }}
                  />
                ) : null}

                {laidOut.map(({ booking, lane, laneCount }) => (
                  <CalendarEvent
                    key={booking.id}
                    state={state}
                    booking={booking}
                    conflict={conflictIds.has(booking.id)}
                    planningLens={planningLens}
                    onSelect={onSelect}
                    layout={{ lane, laneCount }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthGrid({ state, cursor, resourceFilter, onSelect, onCreate }) {
  const { t, localize, formatDate, formatMoney } = useI18n();
  const days = monthGridDays(cursor);
  const today = new Date();
  const selectedMonth = cursor.getMonth();
  const weekdayLabels = Array.from({ length: 7 }, (_, index) => (
    formatDate(addDays(new Date(2026, 7, 24), index), { weekday: "short" })
  ));

  return (
    <div className="overflow-auto bg-surface">
      <div className="min-w-[980px]">
        <div className="grid grid-cols-7 border-b border-border bg-muted/30">
          {weekdayLabels.map((label) => (
            <div key={label} className="border-l border-border px-3 py-2 text-xs font-semibold text-muted-foreground first:border-l-0">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const bookings = state.bookings
              .filter((booking) => sameDay(booking.start, day))
              .filter((booking) => resourceFilter === "all" || bookingMatchesResource(booking, resourceFilter, state.services))
              .sort((a, b) => new Date(a.start) - new Date(b.start));
            const active = bookings.filter((booking) => activeBooking(booking) && booking.state !== "noshow");
            const value = active.reduce((sum, booking) => sum + Number(booking.price || 0), 0);
            const outsideMonth = day.getMonth() !== selectedMonth;
            const isToday = sameDay(day, today);
            return (
              <div
                key={day.toISOString()}
                className={`min-h-36 border-b border-l border-border p-2 first:border-l-0 ${outsideMonth ? "bg-muted/25" : "bg-surface"}`}
              >
                <div className="flex items-center justify-between gap-2 px-1">
                  <button
                    type="button"
                    onClick={() => onCreate({ date: day })}
                    className={`inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-xs font-semibold tabular-nums outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring ${isToday ? "bg-primary text-primary-foreground" : outsideMonth ? "text-muted-foreground" : "text-foreground"}`}
                    aria-label={t("calendar.month.addDate", { date: formatDate(day, { day: "numeric", month: "short" }) })}
                  >
                    {day.getDate()}
                  </button>
                  <span className="text-[10px] font-semibold tabular-nums text-primary-soft-foreground">{value ? formatMoney(value) : ""}</span>
                </div>
                <div className="mt-1.5 space-y-1">
                  {bookings.slice(0, 3).map((booking) => {
                    const customer = state.customers.find((item) => item.id === booking.customerId);
                    const attendance = getAttendanceSignal(booking);
                    const warning = isLowShowProbability(booking) || ["pending", "hold", "noshow"].includes(booking.state);
                    return (
                      <button
                        key={booking.id}
                        type="button"
                        onClick={() => onSelect(booking.id)}
                        className={`flex w-full items-center gap-1 rounded-md border px-1.5 py-1 text-left text-[10px] outline-none transition-colors hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring ${STATE_STYLE[booking.state]}`}
                      >
                        {warning ? <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
                        <span className="shrink-0 font-semibold tabular-nums">{fmtTime(booking.start)}</span>
                        <span className="min-w-0 flex-1 truncate font-semibold">{localize(customer?.name)}</span>
                        <span className="shrink-0 font-medium opacity-75">{Number.isFinite(attendance?.probability) ? `${attendance.probability}%` : localize(attendance?.shortLabel)}</span>
                      </button>
                    );
                  })}
                  {bookings.length > 3 ? (
                    <p className="w-full px-1 text-left text-[10px] font-semibold text-primary">
                      {t("calendar.month.moreAppointments", { count: bookings.length - 3 })}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BookingDrawer({ booking, onClose, onRebook }) {
  const { state, actions } = useStore();
  const { t, localize, formatDate, formatMoney } = useI18n();
  const navigate = useNavigate();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  if (!booking) return null;

  const customer = state.customers.find((item) => item.id === booking.customerId);
  const service = state.services.find((item) => item.id === booking.serviceId);
  const assignments = bookingAssignments(booking, state.services, state.resources);
  const doctor = state.resources.find((resource) => resource.id === assignments.doctorId);
  const room = state.resources.find((resource) => resource.id === assignments.roomId);
  const conversation = state.conversations.find((item) => item.bookingId === booking.id);
  const isPast = new Date(booking.start) <= new Date();
  const valueLabel = booking.state === "completed"
    ? t("calendar.drawer.attendedValue")
    : booking.state === "noshow"
      ? t("calendar.drawer.missedValue")
      : booking.state === "cancelled"
        ? t("calendar.drawer.cancelledValue")
        : t("calendar.drawer.bookedValue");

  return (
    <Sheet open={!!booking} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="!w-full max-w-none overflow-y-auto scroll-thin sm:!max-w-md">
        <SheetHeader>
          <SheetTitle>{localize(customer?.name)}</SheetTitle>
          <SheetDescription>{localize(service?.name)}</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          <div className="rounded-xl border border-primary/25 bg-primary-soft p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-medium text-primary-soft-foreground">{valueLabel}</p>
              <SourceTag source={booking.priceSource || service?.source || "staff"} />
            </div>
            <p className="mt-1 font-sans text-3xl font-semibold tracking-tight text-foreground tabular-nums">{formatMoney(booking.price)}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t("calendar.drawer.valueSnapshot")}</p>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <BookingStateBadge state={booking.state} />
            <AttendanceBadge booking={booking} />
            <RiskBadge risk={booking.risk} source={booking.source} />
            <Chip tone={booking.source === "ilia" ? "ilia" : "human"}>
              {t(booking.source === "ilia" ? "calendar.drawer.bookedByIlia" : "calendar.drawer.bookedByOperator")}
            </Chip>
          </div>

          <div className="space-y-2.5 rounded-xl border border-border bg-background p-3.5 text-sm">
            <Line label={t("calendar.drawer.when")} value={`${formatDate(booking.start, { day: "numeric", month: "long", year: "numeric" })} · ${t("calendar.event.timeRange", { start: fmtTime(booking.start), end: fmtTime(booking.end) })}`} />
            <Line label={t("calendar.drawer.doctor")} value={localize(doctor?.name) || t("calendar.day.notAssigned")} />
            <Line label={t("calendar.drawer.room")} value={localize(room?.name) || t("calendar.day.notAssigned")} />
            <Line label={t("calendar.drawer.duration")} value={t("calendar.booking.minutes", { minutes: bookingDurationMinutes(booking) })} />
            <Line label={t("calendar.drawer.buffer")} value={t("calendar.drawer.minutesAfter", { minutes: booking.buffer })} />
            <Line label={t(booking.state === "completed" || booking.state === "noshow" ? "calendar.drawer.attendance" : "calendar.drawer.estimatedAttendance")} value={localize(getAttendanceSignal(booking)?.label)} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("calendar.drawer.commercialContext")}</p>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border">
              <div className="bg-surface p-3">
                <p className="text-[11px] text-muted-foreground">{t("calendar.drawer.potentialSale")}</p>
                <p className="mt-1 font-sans text-base font-semibold text-foreground tabular-nums">{formatMoney(customer?.value)}</p>
              </div>
              <div className="bg-surface p-3">
                <p className="text-[11px] text-muted-foreground">{t("calendar.drawer.customerHistory")}</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{t("calendar.drawer.customerVisits", {
                  visits: customer?.history.visits || 0,
                  noShows: customer?.history.noShows || 0,
                  moved: customer?.history.moved || 0,
                })}</p>
              </div>
            </div>
          </div>

          {conversation ? (
            <LinkedConversationPreview
              conversation={conversation}
              onOpen={() => navigate(`/inbox/${conversation.id}`)}
            />
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            {(booking.state === "hold" || booking.state === "pending") ? (
              <Button className="col-span-2" onClick={() => actions.markBooking(booking.id, "confirmed")}>
                <Check className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.confirm")}
              </Button>
            ) : null}
            {booking.state === "confirmed" && isPast ? (
              <>
                <Button variant="outline" onClick={() => actions.markBooking(booking.id, "completed")}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.attended")}
                </Button>
                <Button variant="outline" onClick={() => actions.markBooking(booking.id, "noshow")}>
                  <UserX className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.noShow")}
                </Button>
              </>
            ) : null}
            {!["cancelled", "completed", "noshow"].includes(booking.state) ? (
              <Button variant="outline" onClick={() => setRescheduleOpen(true)}>
                <RefreshCcw className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.reschedule")}
              </Button>
            ) : null}
            {!["cancelled", "completed"].includes(booking.state) && booking.state !== "noshow" ? (
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive-soft"
                onClick={() => actions.cancelBooking(booking.id, service?.name)}
              >
                <X className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.cancel")}
              </Button>
            ) : null}
            {booking.state === "noshow" ? (
              <Button className="col-span-2" variant="outline" onClick={() => onRebook(booking)}>
                <RefreshCcw className="mr-1.5 h-4 w-4" /> {t("calendar.drawer.createNew")}
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">{t("calendar.drawer.undoHint")}</p>
        </div>

        <RescheduleDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          booking={booking}
          customer={customer}
          onDone={onClose}
        />
      </SheetContent>
    </Sheet>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{value}</span>
    </div>
  );
}

function LinkedConversationPreview({ conversation, onOpen }) {
  const { t } = useI18n();
  const recentMessages = conversation.messages.filter((message) => message.from !== "system").slice(-3);
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-background" aria-label={t("calendar.conversation.preview")}>
      <div className="flex items-center justify-between gap-3 border-b border-border px-3.5 py-3">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{t("calendar.conversation.preview")}</p>
        </div>
        <ChannelBadge channel={conversation.channel} />
      </div>
      <div className="space-y-2.5 px-3.5 py-3">
        {recentMessages.map((message) => {
          const speaker = message.from === "customer" ? t("calendar.conversation.customer") : message.from === "ilia" ? "Ilia" : message.author || t("calendar.conversation.team");
          return (
            <div key={message.id} className="grid grid-cols-[54px_minmax(0,1fr)] gap-2 text-xs">
              <span className="font-medium text-muted-foreground">{speaker}</span>
              <div className="min-w-0">
                <p className="line-clamp-2 leading-relaxed text-foreground">{message.text}</p>
                {message.slotOptions?.length ? (
                  <p className="mt-1 font-medium text-primary">{t("calendar.conversation.timesOffered", { count: message.slotOptions.length })}</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <Button variant="ghost" className="w-full justify-between rounded-none border-t border-border px-3.5" onClick={onOpen}>
        <span>{t("calendar.conversation.openFull")}</span>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </section>
  );
}

function RescheduleDialog({ open, onOpenChange, booking, customer, onDone }) {
  const { state, actions } = useStore();
  const { t, localize, formatDate } = useI18n();
  const initialDate = addDays(new Date(booking.start), 1);
  const [date, setDate] = useState(dateInputValue(initialDate));
  const [time, setTime] = useState(timeInputValue(booking.start));
  const service = state.services.find((item) => item.id === booking.serviceId);
  const assignments = bookingAssignments(booking, state.services, state.resources);
  const [assignmentIntent, setAssignmentIntent] = useState("doctor");
  const [requestedDoctorId, setRequestedDoctorId] = useState(assignments.doctorId || "");
  const [requestedRoomId, setRequestedRoomId] = useState(assignments.roomId || "");
  const doctorIds = serviceDoctorIds(service, state.roomDuties);
  const roomIds = serviceRoomIds(service, state.roomDuties);
  const defaultDoctorId = assignments.doctorId || doctorIds[0] || "";
  const defaultRoomId = assignments.roomId || roomIds[0] || "";

  useEffect(() => {
    if (!open) return;
    setDate(dateInputValue(addDays(new Date(booking.start), 1)));
    setTime(timeInputValue(booking.start));
    setAssignmentIntent("doctor");
    setRequestedDoctorId(defaultDoctorId);
    setRequestedRoomId(defaultRoomId);
  }, [open, booking.id, booking.start, defaultDoctorId, defaultRoomId]);

  const start = localDateTime(date, time);
  const duration = bookingDurationMinutes(booking);
  const end = start ? new Date(start.getTime() + duration * 60000) : null;
  const bufferEnd = end ? new Date(end.getTime() + Number(booking.buffer || 0) * 60000) : null;
  const assignment = start && end && service && !(assignmentIntent === "doctor" && !requestedDoctorId)
    ? resolveDutyAssignment({
        duties: state.roomDuties,
        service,
        start,
        end,
        buffer: booking.buffer,
        doctorId: assignmentIntent === "doctor" ? requestedDoctorId || null : null,
        roomId: assignmentIntent === "service" ? requestedRoomId || null : null,
      })
    : null;
  const doctorId = assignment?.doctorId || "";
  const roomId = assignment?.roomId || "";
  const candidate = start && end && assignment ? {
    ...booking,
    doctorId,
    roomId,
    resourceId: doctorId,
    resourceIds: [doctorId, roomId].filter(Boolean),
    start: start.toISOString(),
    end: end.toISOString(),
  } : null;
  const conflictDetails = candidate
    ? findBookingConflictDetails(state.bookings, state.services, candidate, booking.id)
    : [];
  const dutyIssue = candidate
    ? findDutyAssignmentIssue(state.roomDuties, candidate)
    : null;
  const closed = start?.getDay() === 0;
  const inPast = end ? end <= new Date() : false;
  const outsideHours = start && bufferEnd
    ? decimalHour(start.toISOString()) < START || decimalHour(bufferEnd.toISOString()) > END
    : true;
  const requestedDoctorMissing = assignmentIntent === "doctor" && !requestedDoctorId;
  const assignmentsComplete = !!assignment;
  const available = !!candidate
    && assignmentsComplete
    && !closed
    && !inPast
    && !outsideHours
    && !dutyIssue
    && conflictDetails.length === 0;
  const doctor = state.resources.find((resource) => resource.id === doctorId);
  const room = state.resources.find((resource) => resource.id === roomId);
  const whenLabel = start
    ? `${formatDate(start, { day: "numeric", month: "short" })} · ${fmtTime(start)}`
    : t("calendar.form.selectedTime");
  const notification = t("calendar.form.rescheduleNotice", {
    customer: localize(customer?.name),
    service: localize(service?.name),
    when: whenLabel,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b border-border px-6 py-5 text-left">
          <DialogTitle>{t("calendar.reschedule.title")}</DialogTitle>
          <DialogDescription>{t("calendar.reschedule.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5">
          <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-3.5">
            <div>
              <Label>{t("calendar.assignment.requested")}</Label>
              <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{t("calendar.assignment.help")}</p>
            </div>
            <div className="inline-flex max-w-full flex-wrap rounded-lg border border-border bg-muted p-1" role="group" aria-label={t("calendar.assignment.intent")}>
              <button
                type="button"
                aria-pressed={assignmentIntent === "service"}
                onClick={() => setAssignmentIntent("service")}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${assignmentIntent === "service" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t("calendar.assignment.serviceOrRoom")}
              </button>
              <button
                type="button"
                aria-pressed={assignmentIntent === "doctor"}
                onClick={() => {
                  setAssignmentIntent("doctor");
                  if (!requestedDoctorId) setRequestedDoctorId(defaultDoctorId);
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${assignmentIntent === "doctor" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t("calendar.assignment.specificDoctor")}
              </button>
            </div>

            {assignmentIntent === "doctor" ? (
              <div className="space-y-2">
                <Label htmlFor="reschedule-requested-doctor">{t("calendar.assignment.requestedDoctor")}</Label>
                <select id="reschedule-requested-doctor" value={requestedDoctorId} onChange={(event) => setRequestedDoctorId(event.target.value)} className={FIELD_CLASS}>
                  <option value="">{t("calendar.assignment.chooseDoctor")}</option>
                  {doctorIds.map((id) => {
                    const resource = state.resources.find((item) => item.id === id);
                    return resource ? <option key={id} value={id}>{localize(resource.name)}</option> : null;
                  })}
                </select>
                <p className="text-[11px] leading-4 text-muted-foreground">{t("calendar.assignment.doctorRotaHelp")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="reschedule-requested-room">{t("calendar.assignment.preferredRoom")}</Label>
                <select id="reschedule-requested-room" value={requestedRoomId} onChange={(event) => setRequestedRoomId(event.target.value)} className={FIELD_CLASS}>
                  <option value="">{t("calendar.assignment.anyRoom")}</option>
                  {roomIds.map((id) => {
                    const resource = state.resources.find((item) => item.id === id);
                    return resource ? <option key={id} value={id}>{localize(resource.name)}</option> : null;
                  })}
                </select>
                <p className="text-[11px] leading-4 text-muted-foreground">{t("calendar.assignment.roomDoctorHelp")}</p>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">{t("calendar.form.date")}</Label>
              <Input id="reschedule-date" type="date" min={dateInputValue(new Date())} value={date} onChange={(event) => setDate(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reschedule-time">{t("calendar.form.startTime")}</Label>
              <Input id="reschedule-time" type="time" min="10:00" max="19:00" step="900" value={time} onChange={(event) => setTime(event.target.value)} />
            </div>
          </div>

          <div className={`rounded-xl border p-3.5 ${available ? "border-success/35 bg-success-soft" : "border-destructive/35 bg-destructive-soft"}`}>
            <div className="flex items-start gap-2.5">
              {available
                ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">{t(available ? "calendar.form.slotAvailable" : "calendar.form.slotUnavailable")}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {closed
                    ? t("calendar.booking.closedSunday")
                    : requestedDoctorMissing
                      ? t("calendar.booking.chooseSpecificDoctor")
                    : inPast
                      ? t("calendar.booking.chooseFuture")
                    : outsideHours
                      ? t("calendar.form.chooseHours", { start: START, end: END })
                      : !assignmentsComplete
                        ? t("calendar.booking.noDutyCoverage")
                      : dutyIssue
                        ? localizedDutyIssueMessage(dutyIssue, state.resources, t, localize, formatDate)
                      : conflictDetails.length
                        ? conflictSummary(conflictDetails, state, t, localize)
                        : t("calendar.form.assignmentAvailable", {
                            doctor: localize(doctor?.short),
                            room: localize(room?.short),
                            buffer: booking.buffer,
                          })}
                </p>
              </div>
              <SourceTag source="derived" />
            </div>
          </div>

          {available ? (
            <div className="rounded-xl border border-border bg-muted/45 p-3.5 text-sm">
              <p className="mb-1 text-xs font-medium text-muted-foreground">{t("calendar.form.notificationPreview")}</p>
              {notification}
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t border-border bg-surface px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("calendar.form.cancel")}</Button>
          <Button
            disabled={!available}
            onClick={() => {
              const success = actions.reschedule({
                id: booking.id,
                start: start.toISOString(),
                end: end.toISOString(),
                doctorId,
                roomId,
                resourceId: doctorId,
                resourceIds: [doctorId, roomId],
                actor: "human",
                label: t("calendar.form.rescheduleLabel", { when: whenLabel }),
                notify: notification,
              });
              if (success) {
                onOpenChange(false);
                onDone?.();
              }
            }}
          >
            {t("calendar.form.confirmNotify")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewBookingSheet({ seed, onClose, onCreated }) {
  const { state, actions } = useStore();
  const { t, localize, formatDate, formatMoney } = useI18n();
  const seededService = state.services.find((item) => item.id === seed.serviceId);
  const initialAssignmentIntent = seed.assignmentIntent === "doctor" ? "doctor" : "service";
  const [customerSearch, setCustomerSearch] = useState("");
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [form, setForm] = useState({
    customerId: seed.customerId,
    serviceId: seed.serviceId,
    assignmentIntent: initialAssignmentIntent,
    requestedDoctorId: initialAssignmentIntent === "doctor"
      ? seed.doctorId || seededService?.resource || ""
      : "",
    requestedRoomId: initialAssignmentIntent === "service" ? seed.roomId || "" : "",
    date: seed.date,
    time: seed.time,
    state: "confirmed",
    price: seededService?.price ?? "",
    priceEdited: false,
    notify: true,
  });

  const customer = state.customers.find((item) => item.id === form.customerId);
  const customerMatches = useMemo(() => {
    const normalized = customerSearch.trim().toLowerCase();
    return [...state.customers]
      .filter((item) => !normalized || `${item.name} ${localize(item.name)} ${item.phone || ""} ${item.email || ""}`.toLowerCase().includes(normalized))
      .sort((a, b) => Number(b.id === form.customerId) - Number(a.id === form.customerId) || localize(a.name).localeCompare(localize(b.name)))
      .slice(0, 6);
  }, [customerSearch, form.customerId, localize, state.customers]);
  const service = state.services.find((item) => item.id === form.serviceId);
  const doctorIds = serviceDoctorIds(service, state.roomDuties);
  const roomIds = serviceRoomIds(service, state.roomDuties);
  const start = localDateTime(form.date, form.time);
  const end = start && service
    ? new Date(start.getTime() + Number(service.duration) * 60000)
    : null;
  const bufferEnd = end && service
    ? new Date(end.getTime() + Number(service.buffer || 0) * 60000)
    : null;
  const assignment = start && end && service && !(form.assignmentIntent === "doctor" && !form.requestedDoctorId)
    ? resolveDutyAssignment({
        duties: state.roomDuties,
        service,
        start,
        end,
        buffer: service.buffer,
        doctorId: form.assignmentIntent === "doctor" ? form.requestedDoctorId || null : null,
        roomId: form.assignmentIntent === "service" ? form.requestedRoomId || null : null,
      })
    : null;
  const resolvedDoctorId = assignment?.doctorId || "";
  const resolvedRoomId = assignment?.roomId || "";
  const candidate = start && end && service && assignment ? {
    id: "candidate",
    customerId: form.customerId,
    serviceId: service.id,
    doctorId: resolvedDoctorId,
    roomId: resolvedRoomId,
    resourceId: resolvedDoctorId,
    resourceIds: [resolvedDoctorId, resolvedRoomId].filter(Boolean),
    start: start.toISOString(),
    end: end.toISOString(),
    buffer: service.buffer,
    state: form.state,
  } : null;
  const conflictDetails = candidate
    ? findBookingConflictDetails(state.bookings, state.services, candidate)
    : [];
  const dutyIssue = candidate
    ? findDutyAssignmentIssue(state.roomDuties, candidate)
    : null;
  const closed = start?.getDay() === 0;
  const inPast = end ? end <= new Date() : false;
  const outsideHours = start && bufferEnd
    ? decimalHour(start.toISOString()) < START || decimalHour(bufferEnd.toISOString()) > END
    : false;
  const doctor = state.resources.find((resource) => resource.id === resolvedDoctorId);
  const room = state.resources.find((resource) => resource.id === resolvedRoomId);
  const numericPrice = Number(form.price);
  const requestedDoctorMissing = form.assignmentIntent === "doctor" && !form.requestedDoctorId;
  const assignmentsComplete = !!assignment;
  const valid = !!customer
    && !!service
    && !!candidate
    && assignmentsComplete
    && numericPrice > 0
    && !closed
    && !inPast
    && !outsideHours
    && !dutyIssue
    && conflictDetails.length === 0;
  const whenLabel = start
    ? `${formatDate(start, { day: "numeric", month: "short" })} · ${fmtTime(start)}`
    : t("calendar.form.selectedTime");
  const bookingPhrase = form.state === "confirmed"
    ? t("calendar.booking.noticeConfirmed", { when: whenLabel })
    : form.state === "hold"
      ? t("calendar.booking.noticeHeld", { when: whenLabel })
      : t("calendar.booking.noticePending", { when: whenLabel });
  const notificationAssignment = doctor && room
    ? t("calendar.booking.noticeAssignment", { doctor: localize(doctor.short), room: localize(room.short) })
    : "";
  const notification = customer && service
    ? t("calendar.booking.notice", {
        customer: localize(customer.name),
        service: localize(service.name),
        phrase: bookingPhrase,
        assignment: notificationAssignment,
        value: formatMoney(numericPrice),
      })
    : "";

  const changeService = (serviceId) => {
    const nextService = state.services.find((item) => item.id === serviceId);
    const nextDoctors = serviceDoctorIds(nextService, state.roomDuties);
    const nextRooms = serviceRoomIds(nextService, state.roomDuties);
    setForm((current) => ({
      ...current,
      serviceId,
      requestedDoctorId: nextDoctors.includes(current.requestedDoctorId)
        ? current.requestedDoctorId
        : current.assignmentIntent === "doctor" ? nextDoctors[0] || "" : "",
      requestedRoomId: nextRooms.includes(current.requestedRoomId) ? current.requestedRoomId : "",
      price: nextService?.price ?? "",
      priceEdited: false,
    }));
  };

  const createBooking = () => {
    if (!valid) return;
    const id = uid("bk");
    const booking = {
      id,
      customerId: form.customerId,
      serviceId: service.id,
      doctorId: resolvedDoctorId,
      roomId: resolvedRoomId,
      resourceId: resolvedDoctorId,
      resourceIds: bookingResourceIds(candidate, state.services),
      start: start.toISOString(),
      end: end.toISOString(),
      buffer: Number(service.buffer || 0),
      state: form.state,
      source: "human",
      price: numericPrice,
      priceSource: form.priceEdited ? "staff" : service.source,
      risk: 0,
      rebookedFrom: seed.rebookedFrom || null,
    };
    const success = actions.addBooking({ booking, notify: form.notify ? notification : null });
    if (success) {
      onCreated(id);
      onClose();
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex !w-full max-w-none flex-col gap-0 p-0 sm:!max-w-lg">
        <SheetHeader className="border-b border-border px-6 pb-5 pt-6 text-left">
          <SheetTitle>{t(seed.rebookedFrom ? "calendar.booking.createNew" : "calendar.booking.title")}</SheetTitle>
          <SheetDescription>
            {seed.rebookedFrom
              ? t("calendar.booking.rebookDescription")
              : t("calendar.booking.standardDescription")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5 scroll-thin">
          <section className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("calendar.booking.customerAndValue")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("calendar.booking.valueHelp")}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="booking-customer-search">{t("calendar.booking.customer")}</Label>
                <Button type="button" size="sm" variant="ghost" className="h-8 whitespace-nowrap" onClick={() => setNewCustomerOpen(true)}>
                  <UserPlus className="mr-1.5 h-4 w-4" /> {t("calendar.booking.newCustomer")}
                </Button>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="booking-customer-search"
                  value={customerSearch}
                  onChange={(event) => setCustomerSearch(event.target.value)}
                  placeholder={t("calendar.booking.searchCustomer")}
                  className="pl-9"
                  autoComplete="off"
                />
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border bg-background p-1.5 scroll-thin" role="listbox" aria-label={t("calendar.booking.searchResults")}>
                {customerMatches.map((item) => {
                  const selected = item.id === form.customerId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        setForm((current) => ({ ...current, customerId: item.id }));
                        setCustomerSearch(localize(item.name));
                      }}
                      className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "bg-primary-soft text-primary-soft-foreground" : "hover:bg-muted"}`}
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">{item.initials}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">{localize(item.name)}</span>
                        <span className="block truncate text-xs text-muted-foreground">{item.phone || item.email || t("calendar.booking.noContact")}</span>
                      </span>
                      <span className="shrink-0 font-sans text-xs font-semibold text-accent-soft-foreground tabular-nums">{formatMoney(item.value)}</span>
                      {selected ? <Check className="h-4 w-4 shrink-0" /> : null}
                    </button>
                  );
                })}
                {!customerMatches.length ? (
                  <div className="px-3 py-5 text-center">
                    <p className="text-sm font-medium text-foreground">{t("calendar.booking.noCustomer")}</p>
                    <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setNewCustomerOpen(true)}>
                      <UserPlus className="mr-1.5 h-4 w-4" /> {t("calendar.booking.createCustomer")}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border">
              <div className="bg-accent-soft p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <p className="text-[11px] font-medium text-accent-soft-foreground">{t("calendar.booking.potentialSale")}</p>
                  {customer ? <SourceTag source={customer.valueSource || "staff"} /> : null}
                </div>
                <p className="mt-1 font-sans text-xl font-semibold text-foreground tabular-nums">{customer ? formatMoney(customer.value) : t("calendar.booking.notSet")}</p>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{localize(customer?.valueBasis) || t("calendar.booking.chooseCustomer")}</p>
              </div>
              <div className="bg-primary-soft p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <p className="text-[11px] font-medium text-primary-soft-foreground">{t("calendar.booking.bookingValue")}</p>
                  {service ? <SourceTag source={form.priceEdited ? "staff" : service.source} /> : null}
                </div>
                <p className="mt-1 font-sans text-xl font-semibold text-foreground tabular-nums">{numericPrice > 0 ? formatMoney(numericPrice) : t("calendar.booking.notSet")}</p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">{t("calendar.booking.savedOnAppointment")}</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_150px]">
              <div className="space-y-2">
                <Label htmlFor="booking-service">{t("calendar.booking.service")}</Label>
                <select id="booking-service" value={form.serviceId} onChange={(event) => changeService(event.target.value)} className={FIELD_CLASS}>
                  <option value="">{t("calendar.booking.chooseService")}</option>
                  {state.services.map((item) => (
                    <option key={item.id} value={item.id}>{localize(item.name)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-value">{t("calendar.booking.bookingValue")}</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-sans text-sm font-medium text-muted-foreground">₾</span>
                  <Input
                    id="booking-value"
                    type="number"
                    min="1"
                    step="1"
                    value={form.price}
                    onChange={(event) => setForm((current) => ({ ...current, price: event.target.value, priceEdited: true }))}
                    className="pl-7 font-sans tabular-nums"
                  />
                </div>
              </div>
            </div>

            {service ? (
              <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-3.5">
                <div>
                  <Label>{t("calendar.assignment.requested")}</Label>
                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{t("calendar.booking.requestHelp")}</p>
                </div>
                <div className="inline-flex max-w-full flex-wrap rounded-lg border border-border bg-muted p-1" role="group" aria-label={t("calendar.assignment.intent")}>
                  <button
                    type="button"
                    aria-pressed={form.assignmentIntent === "service"}
                    onClick={() => setForm((current) => ({ ...current, assignmentIntent: "service" }))}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${form.assignmentIntent === "service" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {t("calendar.assignment.serviceOrRoom")}
                  </button>
                  <button
                    type="button"
                    aria-pressed={form.assignmentIntent === "doctor"}
                    onClick={() => setForm((current) => ({
                      ...current,
                      assignmentIntent: "doctor",
                      requestedDoctorId: doctorIds.includes(current.requestedDoctorId)
                        ? current.requestedDoctorId
                        : doctorIds[0] || "",
                    }))}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${form.assignmentIntent === "doctor" ? "bg-surface text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {t("calendar.assignment.specificDoctor")}
                  </button>
                </div>

                {form.assignmentIntent === "doctor" ? (
                  <div className="space-y-2">
                    <Label htmlFor="booking-requested-doctor">{t("calendar.assignment.requestedDoctor")}</Label>
                    <select
                      id="booking-requested-doctor"
                      value={form.requestedDoctorId}
                      onChange={(event) => setForm((current) => ({ ...current, requestedDoctorId: event.target.value }))}
                      className={FIELD_CLASS}
                    >
                      <option value="">{t("calendar.assignment.chooseDoctor")}</option>
                      {doctorIds.map((id) => {
                        const resource = state.resources.find((item) => item.id === id);
                        return resource ? <option key={id} value={id}>{localize(resource.name)}</option> : null;
                      })}
                    </select>
                    <p className="text-[11px] leading-4 text-muted-foreground">{t("calendar.booking.doctorRoomHelp")}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="booking-requested-room">{t("calendar.assignment.preferredRoom")}</Label>
                    <select
                      id="booking-requested-room"
                      value={form.requestedRoomId}
                      onChange={(event) => setForm((current) => ({ ...current, requestedRoomId: event.target.value }))}
                      className={FIELD_CLASS}
                    >
                      <option value="">{t("calendar.assignment.anyRoom")}</option>
                      {roomIds.map((id) => {
                        const resource = state.resources.find((item) => item.id === id);
                        return resource ? <option key={id} value={id}>{localize(resource.name)}</option> : null;
                      })}
                    </select>
                    <p className="text-[11px] leading-4 text-muted-foreground">{t("calendar.assignment.serviceRoomHelp")}</p>
                  </div>
                )}
              </div>
            ) : null}
          </section>

          <section className="space-y-4 border-t border-border pt-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("calendar.booking.schedule")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("calendar.booking.workingHours")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="booking-date">{t("calendar.form.date")}</Label>
                <Input id="booking-date" type="date" min={dateInputValue(new Date())} value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="booking-time">{t("calendar.form.startTime")}</Label>
                <Input id="booking-time" type="time" min="10:00" max="19:00" step="900" value={form.time} onChange={(event) => setForm((current) => ({ ...current, time: event.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="booking-state">{t("calendar.booking.initialStatus")}</Label>
              <select id="booking-state" value={form.state} onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))} className={FIELD_CLASS}>
                <option value="confirmed">{t("calendar.booking.statusConfirmed")}</option>
                <option value="pending">{t("calendar.booking.statusPending")}</option>
                <option value="hold">{t("calendar.booking.statusHold")}</option>
              </select>
            </div>

            {service ? (
              <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-3.5 text-sm">
                <Line label={t("calendar.booking.assignedDoctor")} value={localize(doctor?.name) || t("calendar.booking.notResolved")} />
                <Line label={t("calendar.booking.assignedRoom")} value={localize(room?.name) || t("calendar.booking.notResolved")} />
                <Line label={t("calendar.booking.requestType")} value={t(form.assignmentIntent === "doctor" ? "calendar.assignment.specificDoctor" : form.requestedRoomId ? "calendar.booking.serviceAndRoom" : "calendar.booking.serviceOnly")} />
                <Line label={t("calendar.booking.appointment")} value={t("calendar.booking.minutes", { minutes: service.duration })} />
                <Line label={t("calendar.booking.buffer")} value={t("calendar.booking.minutesAfter", { minutes: service.buffer })} />
              </div>
            ) : null}

            {service && start && end ? (
              <div className={`rounded-xl border p-3.5 ${valid ? "border-success/35 bg-success-soft" : "border-destructive/35 bg-destructive-soft"}`}>
                <div className="flex items-start gap-2.5">
                  {valid
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">{t(valid ? "calendar.booking.resourcesFree" : "calendar.booking.needsAttention")}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {closed
                        ? t("calendar.booking.closedSunday")
                        : requestedDoctorMissing
                          ? t("calendar.booking.chooseSpecificDoctor")
                        : inPast
                          ? t("calendar.booking.chooseFuture")
                        : outsideHours
                          ? t("calendar.booking.fitHours", { start: START, end: END })
                          : !assignmentsComplete
                            ? t("calendar.booking.noDutyCoverage")
                          : dutyIssue
                            ? localizedDutyIssueMessage(dutyIssue, state.resources, t, localize, formatDate)
                          : conflictDetails.length
                            ? conflictSummary(conflictDetails, state, t, localize)
                            : numericPrice <= 0
                              ? t("calendar.booking.enterValue")
                              : !customer
                                ? t("calendar.booking.chooseCustomerContinue")
                                : t("calendar.booking.assignmentFree", {
                                    doctor: localize(doctor?.short),
                                    room: localize(room?.short),
                                    time: whenLabel,
                                  })}
                    </p>
                  </div>
                  <SourceTag source="derived" />
                </div>
              </div>
            ) : null}
          </section>

          <section className="space-y-3 border-t border-border pt-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="booking-notify">{t("calendar.booking.notifyCustomer")}</Label>
                <p className="mt-1 text-xs text-muted-foreground">{t("calendar.booking.notifyHelp")}</p>
              </div>
              <Switch id="booking-notify" checked={form.notify} onCheckedChange={(checked) => setForm((current) => ({ ...current, notify: checked }))} />
            </div>
            {form.notify && notification ? (
              <div className="rounded-xl border border-border bg-muted/45 p-3.5 text-sm">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><MessageCircle className="h-3.5 w-3.5" /> {t("calendar.form.notificationPreview")}</p>
                {notification}
              </div>
            ) : null}
          </section>
        </div>

        <div className="border-t border-border bg-surface px-6 py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock3 className="h-3.5 w-3.5" /> {t("calendar.booking.saveCheck")}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{t("calendar.form.cancel")}</Button>
              <Button disabled={!valid} onClick={createBooking}>
                <Plus className="mr-1.5 h-4 w-4" /> {t("calendar.booking.create")}
              </Button>
            </div>
          </div>
        </div>

        <NewCustomerDialog
          open={newCustomerOpen}
          onOpenChange={setNewCustomerOpen}
          initialName={customerSearch}
          onCreate={(fields) => {
            const id = actions.addCustomer(fields);
            setForm((current) => ({ ...current, customerId: id }));
            setCustomerSearch(fields.name.trim());
            setNewCustomerOpen(false);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

function NewCustomerDialog({ open, onOpenChange, initialName, onCreate }) {
  const { t } = useI18n();
  const [fields, setFields] = useState({ name: "", phone: "", email: "", value: "" });
  const contactEntered = fields.phone.trim() || fields.email.trim();
  const valid = fields.name.trim().length >= 2 && !!contactEntered;

  useEffect(() => {
    if (open) setFields((current) => ({ ...current, name: current.name || initialName.trim() }));
  }, [initialName, open]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) setFields({ name: "", phone: "", email: "", value: "" });
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("calendar.customer.title")}</DialogTitle>
          <DialogDescription>{t("calendar.customer.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="new-customer-name">{t("calendar.customer.fullName")}</Label>
            <Input id="new-customer-name" value={fields.name} onChange={(event) => setFields((current) => ({ ...current, name: event.target.value }))} placeholder={t("calendar.customer.namePlaceholder")} autoFocus />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-customer-phone">{t("calendar.customer.phone")}</Label>
              <Input id="new-customer-phone" value={fields.phone} onChange={(event) => setFields((current) => ({ ...current, phone: event.target.value }))} placeholder="+995 5..." type="tel" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-customer-email">{t("calendar.customer.email")}</Label>
              <Input id="new-customer-email" value={fields.email} onChange={(event) => setFields((current) => ({ ...current, email: event.target.value }))} placeholder="name@example.com" type="email" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("calendar.customer.contactHelp")}</p>
          <div className="space-y-2">
            <Label htmlFor="new-customer-value">{t("calendar.customer.potentialSale")}</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-sans text-sm font-medium text-muted-foreground">₾</span>
              <Input id="new-customer-value" type="number" min="0" step="1" value={fields.value} onChange={(event) => setFields((current) => ({ ...current, value: event.target.value }))} className="pl-7 font-sans tabular-nums" placeholder="0" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t("calendar.form.cancel")}</Button>
          <Button disabled={!valid} onClick={() => onCreate(fields)}><UserPlus className="mr-1.5 h-4 w-4" /> {t("calendar.customer.createAndSelect")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
